import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Modal } from "./ui/modal";
import Button from "./ui/button/Button";

export interface ConfirmHandle {
  /**
   * Opens the confirm dialog
   */
  open: () => void;

  /**
   * Closes the confirm dialog
   */
  close: () => void;
}

interface ConfirmProps {
  /**
   * The title of the confirm dialog
   */
  title: string;

  /**
   * Optional subtitle for additional context
   */
  subtitle?: string;

  /**
   * Optional content for the confirm dialog body
   */
  content?: React.ReactNode;

  /**
   * Callback function when the user confirms the action
   */
  onConfirm: () => void | Promise<void>;

  /**
   * Text for the confirm button
   * @default "Confirm"
   */
  confirmText?: string;

  /**
   * Text for the cancel button
   * @default "Cancel"
   */
  cancelText?: string;

  /**
   * Optional CSS class name for additional styling
   */
  className?: string;

  /**
   * Whether the confirm button should be disabled
   * @default false
   */
  confirmDisabled?: boolean;

  /**
   * Variant for the confirm button
   * @default "primary"
   */
  confirmVariant?: "primary" | "outline";

  /**
   * Optional callback when dialog is closed
   */
  onClose?: () => void;
}

/**
 * A reusable confirmation dialog component with imperative handle
 */
const Confirm = forwardRef<ConfirmHandle, ConfirmProps>(
  (
    {
      title,
      subtitle,
      content,
      onConfirm,
      confirmText = "Confirm",
      cancelText = "Cancel",
      className = "",
      confirmDisabled = false,
      confirmVariant = "primary",
      onClose,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    }));

    const handleConfirm = async () => {
      await onConfirm();
      setIsOpen(false);
    };

    const handleCancel = () => {
      setIsOpen(false);
      onClose?.();
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        className={`max-w-md mx-auto ${className}`}
      >
        <div className="p-6">
          <div className="mb-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            {subtitle && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>

          {content && <div className="mb-5">{content}</div>}

          <div className="flex items-center justify-end gap-3 mt-6">
            <Button variant="outline" onClick={handleCancel}>
              {cancelText}
            </Button>
            <Button
              variant={confirmVariant}
              onClick={handleConfirm}
              disabled={confirmDisabled}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
);

export default Confirm;
